import { useState } from 'react';
import type { Store } from '@geely/shared';
import { toast } from 'sonner';

export type BookingStep = 1 | 2 | 3 | 4 | 5;
export type BookingSubStep = 1 | 2 | 3 | 4;

export function useBookingWizard(stores: Store[]) {
  const [step, setStep] = useState<BookingStep>(1);
  const [subStep, setSubStep] = useState<BookingSubStep>(1);

  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [appointmentId, setAppointmentId] = useState('');

  const selectedStore = stores.find((s) => s.id === selectedStoreId);
  const isStepOneComplete = Boolean(selectedStoreId && selectedDate && selectedTime);
  const isLastStep = step >= 5;

  // --- Navegação entre steps ---

  const advanceStep = () => {
    if (!isLastStep) {
      setStep((step + 1) as BookingStep);
    }
  };

  const retreatStep = () => {
    if (step > 1) {
      setStep((step - 1) as BookingStep);
    }
  };

  const validateStepOne = (): boolean => {
    if (!isStepOneComplete) {
      toast.error('Por favor, preencha todos os campos');
      return false;
    }
    return true;
  };

  const validateStepTwo = async (formTrigger?: () => Promise<boolean>): Promise<boolean> => {
    if (!formTrigger) return true;
    return await formTrigger();
  };

  const validateStepThree = async (formTrigger?: () => Promise<boolean>): Promise<boolean> => {
    if (!formTrigger) return true;
    return await formTrigger();
  };

  const handleNext = async (formTrigger?: () => Promise<boolean>) => {
    if (step === 1 && !validateStepOne()) return;
    if (step === 2 && !(await validateStepTwo(formTrigger))) return;
    if (step === 3 && !(await validateStepThree(formTrigger))) return;

    advanceStep();
  };

  // --- Navegação entre sub-steps ---

  const resetFieldForSubStep: Record<number, () => void> = {
    2: () => setSelectedState(''),
    3: () => setSelectedCity(''),
    4: () => setSelectedStoreId(''),
  };

  const handleBackSubStep = () => {
    if (subStep <= 1) return;

    setSubStep((subStep - 1) as BookingSubStep);
    resetFieldForSubStep[subStep]?.();
  };

  const handleBack = () => {
    const isInSubSteps = step === 1 && subStep > 1;

    if (isInSubSteps) {
      handleBackSubStep();
    } else {
      retreatStep();
    }
  };

  // --- Seleções do wizard ---

  const resetDownstreamSelections = (...fields: Array<(value: string) => void>) => {
    fields.forEach((setter) => setter(''));
  };

  const handleStateSelect = (state: string) => {
    setSelectedState(state);
    resetDownstreamSelections(setSelectedCity, setSelectedStoreId);
    setSubStep(2);
  };

  const handleCitySelect = (city: string) => {
    setSelectedCity(city);
    resetDownstreamSelections(setSelectedStoreId);
    setSubStep(3);
  };

  const handleStoreSelect = (storeId: string) => {
    setSelectedStoreId(storeId);
    setSubStep(4);
  };

  const handleDateTimeSelect = (date: string, time: string) => {
    setSelectedDate(date);
    setSelectedTime(time);
  };

  return {
    step,
    setStep,
    subStep,
    setSubStep,
    selectedState,
    selectedCity,
    selectedStoreId,
    selectedDate,
    selectedTime,
    setSelectedTime,
    appointmentId,
    setAppointmentId,
    selectedStore,
    handleNext,
    handleBack,
    handleBackSubStep,
    handleStateSelect,
    handleCitySelect,
    handleStoreSelect,
    handleDateTimeSelect,
  };
}
